import { useState, useEffect, useCallback, useRef, type Dispatch, type SetStateAction } from 'react';
import type { AppNotification, AnnouncementItem, NotificationCategory, InboxActionRequest } from '@overlayqa/shared';
import { deriveCategory, applyInboxResult } from '@overlayqa/shared';
import { useAuth } from '../lib/auth';
import { listNotifications, updateInbox } from '../lib/api/notifications';
import { log } from '@/lib/logger';
import { filterInstallAnnouncements } from '../lib/announcementFilter';
import { apiBaseUrl } from '../lib/apiBaseUrl';
import { useNotificationStream } from './notifications/useNotificationStream';

// Read through lib/apiBaseUrl (the identical expression) rather than inline:
// a module-scope import.meta read cannot be parsed by ts-jest, which is why
// this hook had no behavioural test at all until 2026-09-08 (refactor N0a).
const API_URL = apiBaseUrl();

export function useNotifications() {
  const { getToken, userId, isSignedIn } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [announcements, setAnnouncements] = useState<AnnouncementItem[]>([]);
  const [unreadActivityCount, setUnreadActivityCount] = useState(0);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const revision = useRef(0);
  const account = `${isSignedIn}:${userId ?? ''}`;
  const accountRef = useRef(account);
  accountRef.current = account;
  const changeNotifications: Dispatch<SetStateAction<AppNotification[]>> = useCallback(update => {
    revision.current++;
    setNotifications(update);
  }, []);
  const changeAnnouncements: Dispatch<SetStateAction<AnnouncementItem[]>> = useCallback(update => {
    revision.current++;
    setAnnouncements(update);
  }, []);
  useEffect(() => {
    revision.current++;
    setNotifications([]);
    setAnnouncements([]);
  }, [account]);
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  // Unread non-alert count. The bell's own badge comes from useInboxBadge (cfd19a346);
  // this count stays for the SSE connect-race harness (e2e/sse-connect-race/harness/main.tsx).
  useEffect(() => {
    let activity = 0;
    for (const n of notifications) {
      if (n.isRead) continue;
      const cat: NotificationCategory = n.category || deriveCategory(n.type);
      if (cat !== 'alert') activity++;
    }
    setUnreadActivityCount(activity);
  }, [notifications]);

  // A fetch that overlaps an SSE event or confirmed mutation is retried. It
  // cannot resurrect a dismissed row or overwrite a newer unread state.
  const refreshSerial = useRef(0);
  const onInit = useCallback(async () => {
    const serial = ++refreshSerial.current;
    const owner = accountRef.current;
    setLoading(true);
    setLoadError(false);
    try {
      while (serial === refreshSerial.current && owner === accountRef.current) {
        const version = revision.current;
        const token = await getTokenRef.current();
        if (!token) return;
        const [data, response] = await Promise.all([
          listNotifications(token, { limit: 100 }),
          fetch(`${API_URL}/api/announcements`, { headers: { Authorization: `Bearer ${token}` }, signal: AbortSignal.timeout(15_000) }),
        ]);
        if (!response.ok) throw new Error('Announcement fetch failed');
        const body = await response.json();
        if (serial !== refreshSerial.current || owner !== accountRef.current) return;
        if (version !== revision.current) continue;
        setNotifications(data);
        setAnnouncements(filterInstallAnnouncements(body.data || []));
        return;
      }
    } catch (err) {
      if (serial === refreshSerial.current && owner === accountRef.current) setLoadError(true);
      log.warn('[useNotifications] Refresh failed', { error: err instanceof Error ? err.message : String(err) });
    } finally {
      if (serial === refreshSerial.current && owner === accountRef.current) setLoading(false);
    }
  }, []);
  useEffect(() => () => { refreshSerial.current++; }, []);
  useNotificationStream({ setNotifications: changeNotifications, setAnnouncements: changeAnnouncements, onInit });

  const mutateInbox = useCallback(async (request: InboxActionRequest) => {
    const owner = accountRef.current;
    const token = await getTokenRef.current();
    if (!token || owner !== accountRef.current) throw new Error('Sign in to manage notifications');
    const version = revision.current;
    const result = await updateInbox(token, request);
    if (owner !== accountRef.current) throw new Error('Account changed');
    if (version === revision.current) {
      changeNotifications(current => applyInboxResult(current, 'notification', result));
      changeAnnouncements(current => applyInboxResult(current, 'announcement', result));
    } else await onInit();
    return result;
  }, [changeNotifications, changeAnnouncements, onInit]);

  return {
    accountKey: account, mutateInbox, loading, loadError, reload: onInit,
    notifications,
    announcements,
    unreadActivityCount,
  };
}
